
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TestResponsesTable } from "@/components/test-details/TestResponsesTable";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DeleteTestDialog } from "@/components/job/DeleteTestDialog";
import { ShareTestDialog } from "@/components/job/ShareTestDialog";
import { ArrowLeft, ChevronDown, ChevronUp, Trash2, UserPlus, Play, Pause, Users, Shield, Globe, Lock, MapPin, Clock, DollarSign, Briefcase, Building, Eye, Download, MoreHorizontal, CalendarClock, Timer, Copy } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { formatScheduledTime } from "@/utils/scheduling";
import { differenceInMinutes, differenceInHours, differenceInDays, isPast, isFuture } from "date-fns";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { AssessmentPreview } from "@/components/job/preview-sections/AssessmentPreview";
import { showSuccessToast } from "@/utils/toast-utils";
import { useTestStatusActions } from "@/hooks/useTestStatusActions";
import { Question } from "@/types/test";
import { useTestPermissions } from "@/hooks/useTestPermissions";
import { InviteCollaboratorDialog } from "@/components/collaboration/InviteCollaboratorDialog";
import { getPermissionLabel } from "@/utils/collaboration";
import { EditTestDetailsDialog } from "@/components/test-details/EditTestDetailsDialog";
import { TestAnalyticsSummary } from "@/components/test-details/TestAnalyticsSummary";
import { useTestData } from "@/hooks/useTestData";
import { useToast } from "@/hooks/use-toast";
import { transformTestResponse } from "@/utils/response-utils";
import { useDuplicateTest } from "@/hooks/useDuplicateTest";

export default function TestDetails() {
  const { testId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [inviteCollabDialogOpen, setInviteCollabDialogOpen] = useState(false);
  const [isQuestionsOpen, setIsQuestionsOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [countdown, setCountdown] = useState<string>("");
  const isMobile = useIsMobile();
  const { handleToggleStatus, handleDeleteTest, handleToggleVisibility, isUpdating } = useTestStatusActions(testId);
  const { toast } = useToast();
  const { useAllResponsesQuery } = useTestData(testId);
  const { duplicateTest, isDuplicating } = useDuplicateTest();
  
  // Get test permissions for collaboration
  const { data: permissions, isLoading: isLoadingPermissions } = useTestPermissions(testId || '');

  useEffect(() => {
    if (testId) {
      queryClient.invalidateQueries({ queryKey: ['test-responses', testId] });
      queryClient.invalidateQueries({ queryKey: ['test-response-stats', testId] });
    }
  }, [testId, queryClient]);

  useEffect(() => {
    const handleStatusUpdate = () => {
      if (testId) {
        queryClient.invalidateQueries({ queryKey: ['test-response-stats', testId] });
      }
    };

    window.addEventListener('recruitmentStatusUpdated', handleStatusUpdate);
    return () => {
      window.removeEventListener('recruitmentStatusUpdated', handleStatusUpdate);
    };
  }, [testId, queryClient]);

  const { data: test, isLoading: isLoadingTest } = useQuery({
    queryKey: ['test', testId],
    queryFn: async () => {
      if (!testId) return null;
      const { data, error } = await supabase
        .from('tests')
        .select(`
          *,
          creator:profiles!tests_creator_id_fkey (
            id,
            role
          )
        `)
        .eq('id', testId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!testId,
  });

  const { data: questions, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ['test-questions', testId],
    queryFn: async () => {
      if (!testId) return [];
      const { data, error } = await supabase
        .from('questions')
        .select(`
          id,
          question_type,
          content,
          time_limit,
          order,
          marks,
          skill,
          difficulty,
          markscheme
        `)
        .eq('test_id', testId)
        .order('order', { ascending: true });

      if (error) throw error;
      
      const formattedQuestions = (data || []).map(q => ({
        id: q.id,
        question_type: q.question_type,
        content: q.content,
        time_limit: q.time_limit,
        order: q.order,
        marks: q.marks || 1,
        skill: q.skill || null,
        difficulty: (q.difficulty as 'easy' | 'medium' | 'hard' | null) || null,
        markscheme: q.markscheme || null
      })) as Question[];
      
      return formattedQuestions;
    },
    enabled: !!testId,
  });

  const { data: responseStats = { pending: 0, shortlisted: 0, hired: 0, rejected: 0 }, refetch: refetchStats } = useQuery({
    queryKey: ['test-response-stats', testId],
    queryFn: async () => {
      if (!testId) return { pending: 0, shortlisted: 0, hired: 0, rejected: 0 };
      
      const { data, error } = await supabase
        .from('test_responses')
        .select('recruitment_status')
        .eq('test_id', testId);

      if (error) throw error;
      
      const stats = {
        pending: 0,
        shortlisted: 0, 
        hired: 0,
        rejected: 0
      };
      
      if (data) {
        data.forEach((item) => {
          const status = item.recruitment_status || 'pending';
          if (status in stats) {
            stats[status as keyof typeof stats]++;
          }
        });
      }
      
      return stats;
    },
    enabled: !!testId,
    refetchInterval: 30000,
  });

  useEffect(() => {
    refetchStats();
  }, [refetchStats]);

  // Countdown timer for scheduled tests
  useEffect(() => {
    if (!test?.is_scheduled || !test?.scheduled_start_time) {
      setCountdown("");
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const startTime = new Date(test.scheduled_start_time!);
      
      if (isPast(startTime)) {
        setCountdown("");
        return;
      }

      const minutesRemaining = differenceInMinutes(startTime, now);
      const hoursRemaining = differenceInHours(startTime, now);
      const daysRemaining = differenceInDays(startTime, now);

      if (minutesRemaining < 1) {
        setCountdown("Starting soon...");
      } else if (minutesRemaining < 60) {
        setCountdown(`Starts in ${minutesRemaining}m`);
      } else if (hoursRemaining < 24) {
        const mins = minutesRemaining % 60;
        setCountdown(`Starts in ${hoursRemaining}h ${mins}m`);
      } else {
        const hours = hoursRemaining % 24;
        setCountdown(`Starts in ${daysRemaining}d ${hours}h`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [test?.is_scheduled, test?.scheduled_start_time]);

  // Check access permissions
  if (!testId || !test || isLoadingPermissions) {
    return <div className="min-h-screen bg-background">Loading...</div>;
  }

  if (!permissions?.canView) {
    return <div className="min-h-screen bg-background">Access denied</div>;
  }

  const formatSalary = () => {
    if (!test.salary_range_min && !test.salary_range_max) return "Not specified";
    
    const currency = test.salary_currency || "INR";
    
    if (test.salary_range_min && !test.salary_range_max) {
      return `${test.salary_range_min.toLocaleString()} ${currency}+`;
    }
    if (!test.salary_range_min && test.salary_range_max) {
      return `Up to ${test.salary_range_max.toLocaleString()} ${currency}`;
    }
    return `${test.salary_range_min.toLocaleString()} - ${test.salary_range_max.toLocaleString()} ${currency}`;
  };

  const handleShareOpen = () => {
    setShareDialogOpen(true);
    showSuccessToast({
      title: "Share Test",
      description: "Use the dialog to invite candidates to this test."
    });
  };

  const getPermissionColor = (permission: string) => {
    switch (permission) {
      case 'view_responses':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800';
      case 'manage_recruitment':
        return 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-100 dark:border-orange-800';
      case 'full_access':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/20 dark:text-gray-100 dark:border-gray-800';
    }
  };

  const handleExportCSV = async () => {
    if (!testId || !test) return;
    
    setIsExporting(true);
    
    try {
      // Fetch all responses using Supabase directly for CSV export
      const { data: allResponses, error } = await supabase
        .from("test_responses")
        .select(`
          id, status, started_at, completed_at, score, scores_confirmed,
          recruitment_status, recruitment_notes, created_at, integrity_score,
          test_id, candidate_id, recruitment_updated_at,
          candidate:profiles(full_name, email, avatar_url, phone_number, cv_url, location),
          question_responses(
            id, question_id, response_content, score, 
            ai_feedback, evaluation_status, transcription_status, audio_url
          ),
          test:tests(id, title, questions!fk_test(id, marks, order, skill, difficulty, content, question_type, time_limit, markscheme)) 
        `)
        .eq("test_id", testId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }
      
      if (!allResponses || allResponses.length === 0) {
        toast({
          title: "No data to export",
          description: "There are no responses available for this test.",
          variant: "destructive",
        });
        return;
      }

      // Transform the responses using the utility function
      const transformedResponses = allResponses.map((response: any) => 
        transformTestResponse(response, testId, test?.title)
      );

      // Create headers
      const baseHeaders = [
        'Name', 'Email', 'Status', 'Date', 'AI Score (%)', 'Integrity (%)', 
        'Recruitment Status', 'City', 'Phone Number'
      ];
      
      // Add question headers
      const questionHeaders: string[] = [];
      if (questions && questions.length > 0) {
        questions.forEach((_, index) => {
          questionHeaders.push(
            `Question ${index + 1}`,
            `Question ${index + 1} Answer`,
            `Question ${index + 1} AI Score`,
            `Question ${index + 1} Overall Feedback`,
            `Question ${index + 1} Strengths`,
            `Question ${index + 1} Improvements`,
            `Question ${index + 1} Review Points`
          );
        });
      }
      
      const headers = [...baseHeaders, ...questionHeaders];
      
      // Create CSV data
      const csvData = transformedResponses.map(response => {
        const totalMarks = response.test.questions?.reduce((sum: number, q: any) => sum + (q.marks || 1), 0) || 0;
        const aiScore = response.question_responses.reduce((sum: number, qr: any) => {
          return sum + (qr.ai_feedback?.score || 0);
        }, 0);
        const aiScorePercentage = totalMarks > 0 ? Math.round((aiScore / totalMarks) * 100) : 0;

        const baseData = [
          response.candidate.full_name || '',
          response.candidate.email || '',
          response.status || '',
          new Date(response.started_at).toLocaleDateString(),
          `${aiScorePercentage}%`,
          response.integrity_score ? `${response.integrity_score}%` : '',
          response.recruitment_status || 'pending',
          response.candidate.location || '',
          response.candidate.phone_number || '',
        ];

        // Add question data
        const questionData: string[] = [];
        if (questions && questions.length > 0) {
          questions.forEach((question, index) => {
            const questionResponse = response.question_responses.find(
              qr => response.test.questions?.find(q => q.id === qr.question_id && q.order === question.order)
            );
            
            if (questionResponse) {
              // Get question text
              const questionText = typeof question.content === 'object' && question.content 
                ? (question.content as any).question || 'Question text not available'
                : 'Question text not available';
              
              // Get answer text
              let answerText = '';
              if (questionResponse.response_content) {
                if (typeof questionResponse.response_content === 'object') {
                  const content = questionResponse.response_content as any;
                  if (content.selectedAnswer !== undefined) {
                    answerText = String(content.selectedAnswer);
                  } else if (content.text) {
                    answerText = content.text;
                  } else if (content.answer) {
                    answerText = content.answer;
                  } else {
                    answerText = JSON.stringify(content);
                  }
                } else {
                  answerText = String(questionResponse.response_content);
                }
              }
              
              // Extract AI feedback details
              const aiFeedback = questionResponse.ai_feedback;
              const overallFeedback = aiFeedback?.overall_feedback || '';
              const strengths = aiFeedback?.strengths ? aiFeedback.strengths.join('; ') : '';
              const improvements = aiFeedback?.improvements ? aiFeedback.improvements.join('; ') : '';
              const reviewPoints = aiFeedback?.review_points 
                ? `Positive: ${aiFeedback.review_points.positive?.join('; ') || 'None'}. Improvements: ${aiFeedback.review_points.improvement?.join('; ') || 'None'}`
                : '';
              
              questionData.push(
                questionText,
                answerText,
                (questionResponse.ai_feedback?.score || 0).toString(),
                overallFeedback,
                strengths,
                improvements,
                reviewPoints
              );
            } else {
              questionData.push('', '', '0', '', '', '', '');
            }
          });
        }

        return [...baseData, ...questionData];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${test.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_responses_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      toast({
        title: "Export successful",
        description: `Exported ${transformedResponses.length} responses to CSV.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "An error occurred while exporting the data.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className={`container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6`}>
        {/* Header with Back Button and Title */}
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/test-management')}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-foreground line-clamp-2`}>
                {test.title}
              </h1>
              {permissions?.isCollaborator && (
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800 font-medium px-3 py-1 flex items-center gap-1.5"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    Collaborator
                  </Badge>
                  <Badge 
                    variant="outline" 
                    className={`${getPermissionColor(permissions.permission!)} font-medium px-3 py-1`}
                  >
                    {getPermissionLabel(permissions.permission!)}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scheduling Status Card */}
        {test.is_scheduled && (test.scheduled_start_time || test.scheduled_end_time) && (
          <Card className="border-2 bg-gradient-to-r from-primary/5 to-accent/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-foreground">Scheduled Test</span>
                </div>
                
                {test.is_active && test.scheduled_end_time && isFuture(new Date(test.scheduled_end_time)) ? (
                  <Badge className="bg-green-500 text-white border-green-600">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      Live Now
                    </div>
                  </Badge>
                ) : !test.is_active && test.scheduled_start_time && isFuture(new Date(test.scheduled_start_time)) ? (
                  <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20">
                    Scheduled
                  </Badge>
                ) : null}

                <Separator orientation="vertical" className="h-6" />

                <div className="flex items-center gap-6 flex-wrap text-sm">
                  {test.scheduled_start_time && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Starts:</span>
                      <span className="font-medium text-foreground">
                        {formatScheduledTime(new Date(test.scheduled_start_time), test.schedule_timezone || 'UTC')}
                      </span>
                    </div>
                  )}
                  
                  {test.scheduled_end_time && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Ends:</span>
                      <span className="font-medium text-foreground">
                        {formatScheduledTime(new Date(test.scheduled_end_time), test.schedule_timezone || 'UTC')}
                      </span>
                    </div>
                  )}
                </div>

                {countdown && (
                  <>
                    <Separator orientation="vertical" className="h-6" />
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      <Timer className="h-4 w-4" />
                      {countdown}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Toolbar */}
        {permissions?.canView && (
          <Card className="p-4">
            <div className={`flex gap-2 justify-between ${isMobile ? 'flex-wrap' : 'flex-row'}`}>
              {/* Primary Actions Group */}
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="secondary"
                  size="sm"
                  onClick={handleShareOpen}
                  className="gap-1"
                  disabled={isUpdating}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  {isMobile ? 'Invite' : 'Invite Candidates'}
                </Button>
                
                {/* Show collaboration invite button only for owners */}
                {permissions?.isOwner && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInviteCollabDialogOpen(true)}
                    className="gap-1"
                    disabled={isUpdating}
                  >
                    <Users className="h-3.5 w-3.5" />
                    {isMobile ? 'Collab' : 'Collaborate'}
                  </Button>
                )}
                
                {/* Preview Test Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/test/${testId}/take`)}
                  className="gap-1"
                  disabled={isUpdating}
                >
                  <Eye className="h-3.5 w-3.5" />
                  {isMobile ? 'Preview' : 'Preview Test'}
                </Button>
                
                {/* CSV Export - temporarily disabled via feature flag */}
                
                {/* Duplicate button - owners only */}
                {permissions?.isOwner && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => duplicateTest(testId!)}
                    className="gap-1"
                    disabled={isUpdating || isDuplicating}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {isDuplicating ? 'Duplicating...' : (isMobile ? 'Copy' : 'Duplicate')}
                  </Button>
                )}
                
                {/* Show toggle and edit only for owners or full access collaborators */}
                {(permissions?.isOwner || permissions?.canManageTest) && (
                  <>
                    <EditTestDetailsDialog 
                      test={test}
                      canEdit={true}
                    />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(test.is_active)}
                      className="gap-1"
                      disabled={isUpdating}
                    >
                      {test.is_active ? (
                        <>
                          <Pause className="h-4 w-4" />
                          {isMobile ? 'Pause' : 'Deactivate'}
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          {isMobile ? 'Play' : 'Activate'}
                        </>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleVisibility(test.publish_to_job_board)}
                      className="gap-1"
                      disabled={isUpdating}
                    >
                      {test.publish_to_job_board ? (
                        <>
                          <Lock className="h-4 w-4" />
                          {isMobile ? 'Private' : 'Make Private'}
                        </>
                      ) : (
                        <>
                          <Globe className="h-4 w-4" />
                          {isMobile ? 'Public' : 'Make Public'}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>

              {/* Delete Action - Separated */}
              {permissions?.isOwner && (
                <div className="flex items-center gap-2">
                  {/* Show separator on desktop */}
                  {!isMobile && <Separator orientation="vertical" className="h-6" />}
                  
                  {/* On mobile, show delete in More menu */}
                  {isMobile ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="h-8 w-8 p-0"
                          disabled={isUpdating}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuItem 
                          onClick={() => duplicateTest(testId!)}
                          disabled={isDuplicating}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          {isDuplicating ? 'Duplicating...' : 'Duplicate Test'}
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem 
                              className="text-destructive focus:text-destructive cursor-pointer"
                              onSelect={(e) => e.preventDefault()}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Test
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <DeleteTestDialog onConfirm={handleDeleteTest} isDeleting={isUpdating} />
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    /* Desktop delete button */
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-destructive border-destructive/20 hover:bg-destructive hover:text-destructive-foreground h-8 w-8 p-0"
                          disabled={isUpdating}
                          aria-label="Delete test"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <DeleteTestDialog onConfirm={handleDeleteTest} isDeleting={isUpdating} />
                    </AlertDialog>
                  )}
                </div>
              )}
             </div>
           </Card>
        )}
        
        <Collapsible
          open={isDetailsOpen}
          onOpenChange={setIsDetailsOpen}
          className="border rounded-lg bg-card shadow-sm overflow-hidden"
        >
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-muted/20 transition-colors">
              <h3 className="text-xl font-semibold text-foreground">Test Details</h3>
              <div className="flex items-center gap-3">
                {isDetailsOpen ? 
                  <ChevronUp className="h-5 w-5 text-muted-foreground" /> : 
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                }
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-6 pb-6 space-y-6">
              {/* Key Information Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{test.location || "Remote"}</p>
                  </div>
                </div>

                {test.time_limit && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Time Limit</p>
                      <p className="font-medium">{test.time_limit} minutes</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Salary Range</p>
                    <p className="font-medium">{formatSalary()}</p>
                  </div>
                </div>

                {test.work_arrangement && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Work Arrangement</p>
                      <p className="font-medium">{test.work_arrangement}</p>
                    </div>
                  </div>
                )}

                {test.work_presence && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted/30 flex items-center justify-center">
                      <Building className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Work Presence</p>
                      <p className="font-medium">{test.work_presence}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* About the Role */}
              {test.description && (
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">About the Role</h4>
                  <p className="text-foreground leading-relaxed">{test.description}</p>
                </div>
              )}

              {/* Key Responsibilities */}
              {test.key_responsibilities && (
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Key Responsibilities</h4>
                  <p className="text-foreground leading-relaxed">{test.key_responsibilities}</p>
                </div>
              )}

              {/* Required Skills */}
              {test.key_skills && test.key_skills.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-foreground">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {test.key_skills.map((skill: string) => (
                      <Badge 
                        key={skill} 
                        variant="outline" 
                        className="px-4 py-2 rounded-full bg-primary/5 text-primary border-primary/20 hover:bg-primary/10 transition-colors"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        <Collapsible
          open={isQuestionsOpen}
          onOpenChange={setIsQuestionsOpen}
          className="border rounded-sm bg-card shadow-sm"
        >
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/10 transition-colors">
              <h3 className="text-lg font-medium">Test Questions</h3>
              {isQuestionsOpen ? 
                <ChevronUp className="h-5 w-5" /> : 
                <ChevronDown className="h-5 w-5" />
              }
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4">
            {isLoadingQuestions ? (
              <p className="text-muted-foreground">Loading questions...</p>
            ) : questions && questions.length > 0 ? (
              <AssessmentPreview questions={questions} />
            ) : (
              <p className="text-muted-foreground">No questions available</p>
            )}
          </CollapsibleContent>
        </Collapsible>

        {permissions?.canView && (
          <>
            <TestAnalyticsSummary testId={testId} />
            
            <Card>
              <CardHeader className={`${isMobile ? 'flex flex-col items-start' : 'flex flex-row items-center justify-between'}`}>
                <CardTitle>Test Responses</CardTitle>
                <div className="text-sm text-muted-foreground mt-2">
                  {responseStats.pending} pending
                  {' · '}
                  {responseStats.shortlisted} shortlisted
                  {' · '}
                  {responseStats.hired} hired
                  {' · '}
                  {responseStats.rejected} rejected
                </div>
              </CardHeader>
              <CardContent className={isMobile ? 'px-2 py-1' : ''}>
                <TestResponsesTable 
                  testId={testId} 
                  canManageRecruitment={permissions?.canManageRecruitment || false}
                  canDelete={permissions?.isOwner || permissions?.canManageTest || false}
                />
              </CardContent>
            </Card>
          </>
        )}

        <ShareTestDialog 
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          testId={testId}
        />

        {permissions?.isOwner && (
          <InviteCollaboratorDialog
            open={inviteCollabDialogOpen}
            onOpenChange={setInviteCollabDialogOpen}
            testId={testId}
          />
        )}
      </div>
    </div>
  );
}
